
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { getRelativeTime } from '@/components/sidebar/utils';
import { useAuth } from '@/contexts/AuthContext';

type ChatHistoryItem = {
  id: string;
  title: string;
  created_at: string;
};

export function useSidebarData() {
  const [userType, setUserType] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [userInitials, setUserInitials] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const getUserInfo = async () => {
      if (user) {
        // Get user type from profiles table
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('id', user.id)
          .single();
        
        if (profileData?.user_type) {
          setUserType(profileData.user_type);
        }
        
        // Set avatar URL if user signed in with Google
        if (user.app_metadata?.provider === 'google') {
          // Get avatar URL from user metadata
          setAvatarUrl(user.user_metadata?.avatar_url || null);
        }
        
        // Set user initials from email
        const email = user.email || '';
        const initials = email.charAt(0).toUpperCase();
        setUserInitials(initials);

        // Fetch chat history with appropriate filter
        await loadChatHistory(user.id, profileData?.user_type);
      }
    };

    const loadChatHistory = async (userId: string, userType: string | undefined) => {
      try {
        console.log(`Fetching chat history for user ${userId} with type ${userType || 'unknown'}`);

        const { data: history, error } = await supabase
          .from('chat_history')
          .select('id, title, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          console.error('Error fetching chat history:', error);
          toast({
            title: "Error loading chat history",
            description: "Please try refreshing the page.",
            variant: "destructive",
          });
        } else {
          console.log(`Loaded ${history?.length || 0} chat history items`);
          setChatHistory(history || []);
        }
      } catch (error) {
        console.error('Error in loadChatHistory:', error);
      }
    };

    getUserInfo();

    // Set up channel subscription for real-time updates
    const channel = supabase
      .channel('chat_history_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'chat_history',
          filter: user ? `user_id=eq.${user.id}` : undefined
        }, 
        () => {
          if (user) {
            console.log('Detected changes in chat history, reloading...');
            // Only reload the user's own chat history
            loadChatHistory(user.id, userType || undefined);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast, user]);

  return {
    userType,
    avatarUrl,
    userInitials,
    chatHistory,
  };
}
